import { useEffect, useRef } from 'react'

export interface PollingOptions {
	/** Off: nothing runs, e.g. while signed out. */
	enabled?: boolean
	/** Each wait is randomised by up to this fraction either way, so many open tabs do not poll in lockstep. */
	jitter?: number
}

/**
 * Calls `callback` now, then every `intervalMs` while the tab is visible
 * (MYD-542). The app's first data poll; the notification bell uses it.
 *
 * A hidden tab stops polling altogether rather than piling up requests nobody
 * sees. Coming back runs the callback at once, so returning to the app shows
 * what changed without waiting out the interval.
 *
 * The latest `callback` is always used, so it need not be memoised.
 */
export function usePolling(callback: () => void, intervalMs: number, { enabled = true, jitter = 0.1 }: PollingOptions = {}) {
	const latest = useRef(callback)
	useEffect(() => {
		latest.current = callback
	}, [callback])

	useEffect(() => {
		if (!enabled) return undefined

		let timer: ReturnType<typeof setTimeout> | undefined
		const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'

		const schedule = () => {
			clearTimeout(timer)
			const spread = 1 + (Math.random() * 2 - 1) * jitter
			timer = setTimeout(tick, Math.max(1000, intervalMs * spread))
		}

		function tick() {
			if (hidden()) return // resumed by the visibility listener
			latest.current()
			schedule()
		}

		const onVisibility = () => {
			if (hidden()) clearTimeout(timer)
			else tick()
		}

		tick()
		document.addEventListener('visibilitychange', onVisibility)
		return () => {
			clearTimeout(timer)
			document.removeEventListener('visibilitychange', onVisibility)
		}
	}, [enabled, intervalMs, jitter])
}

export default usePolling
